import React, { useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './EventEditor.module.scss'
import attendeesIcon from 'assets/images/calendar/property-attendees.svg'
import addIcon from 'assets/images/menu-add.svg'
import SharedEmailSelect from 'pages/main/components/EmailForm/SharedEmailSelect'
import { selectUser } from 'popups'
import { getUserInfo } from 'pages/calendar/modules/CalendarService'
import { showResult } from 'utils/ui'

interface Attendee {
  name?: string
  email: string
}

interface UserInfo {
  name?: string
  email: string
}

interface EditEventData {
  attendees: Attendee[]
  optionalAttendees: Attendee[]
  isMyEvent: boolean
}

interface AttendeeSelectorProps {
  editEventData: EditEventData
  onAttendeeChange: (userInfoList: UserInfo[]) => void
  isRequired?: boolean
  onOptionalClick?: () => void
  hideOptionalBtn?: boolean
  userDomain: string
}

interface SharedEmailSelectRef {
  setUserInfoList: (userInfoList: UserInfo[]) => void
  getRef: () => HTMLElement
  addUserInfoList: (userInfoList: UserInfo[]) => void
  getValidEmailList: () => string[]
  getUserInfoList: (includeInvalid: boolean) => UserInfo[]
}

interface SelectUserData {
  selectedUserList: Array<{
    name: string
    email: string
  }>
}

const AttendeeSelector: React.FC<AttendeeSelectorProps> = ({
  editEventData,
  onAttendeeChange,
  isRequired = true,
  onOptionalClick,
  hideOptionalBtn = false,
  userDomain,
}) => {
  const selectRef = useRef<SharedEmailSelectRef>(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (selectRef.current) {
      const attendees = isRequired ? editEventData.attendees : editEventData.optionalAttendees
      if (attendees.length === 0) {
        return
      }
      let emailList: UserInfo[] = []
      attendees.forEach((attendee) => {
        if (attendee.name) {
          emailList.push({ name: attendee.name, email: attendee.email })
        } else {
          emailList.push({ email: attendee.email })
        }
      })
      if (emailList.length > 0) {
        selectRef.current.setUserInfoList(emailList)
        setTimeout(() => {
          selectRef.current?.getRef().blur()
        }, 100)
      }
    }
  }, [])

  // useEffect(() => {
  //   if (selectRef.current) {
  //     selectRef.current.getRef().style.width =
  //       selectRef.current.getRef().parentElement.clientWidth + 'px'
  //   }
  // }, [hideOptionalBtn])

  const popupCantAddMyEmail = async () => {
    console.log('showCantAddMyEmail')
    showResult({
      message: t('calendar.edit.cantAddMyEmail'),
    })
  }

  const handleChange = (refCurrent: SharedEmailSelectRef) => {
    const allUserInfoList = refCurrent.getUserInfoList(true)
    console.log(' --- handleChange : ', allUserInfoList)

    const { email: myEmail } = getUserInfo()
    let hasMyEmail = false
    const seenEmails = new Set<string>()
    let hasDuplicate = false

    const filteredList = allUserInfoList.filter((item) => {
      const emailLower = item.email.toLowerCase()
      if (item.email === myEmail) {
        hasMyEmail = true
        return false
      }
      if (seenEmails.has(emailLower)) {
        hasDuplicate = true
        return false
      }
      seenEmails.add(emailLower)
      return true
    })

    if (hasMyEmail || hasDuplicate) {
      if (hasMyEmail) {
        popupCantAddMyEmail()
      }
      refCurrent.setUserInfoList(filteredList)
    }

    onAttendeeChange(filteredList)
  }

  const openSelectUser = (options: { title: string; key?: string }) => {
    const { title } = options

    let ref = selectRef
    const { email: myEmail } = getUserInfo()

    const disableEmails: string[] = [myEmail]
    editEventData.attendees.forEach((attendee) => {
      disableEmails.push(attendee.email)
    })
    editEventData.optionalAttendees.forEach((attendee) => {
      disableEmails.push(attendee.email)
    })

    selectUser({
      userOnly: false,
      title: title || '',
      defaultEmailList: disableEmails,
      excludeEmailList: [],
      disableEmails: disableEmails,
      appendUserTitles: { [myEmail]: t('calendar.attendees.organizer') } as any,
      onOk: (data: SelectUserData, closeFunc: () => void) => {
        console.log(' -- selected user : ', data)
        // const newList = [...shareList]
        const { selectedUserList } = data

        // let hasMyEmail = false
        const userInfoList = selectedUserList
          .filter((item) => {
            if (item.email === myEmail) {
              // hasMyEmail = true
              return false
            }
            return true
          })
          .map((item) => {
            const { name, email } = item
            return { name, email }
          })

        // if (hasMyEmail) {
        // popupCantAddMyEmail()
        // }

        if (ref != null && ref.current != null) {
          ref.current.addUserInfoList(userInfoList)

          setTimeout(() => {
            // 바로 추가하면 validlist 가 이전정보를 가지고 있어서 약간의 Delay를 줌.
            if (ref.current) {
              handleChange(ref.current)
            }
          }, 200)
        }

        closeFunc()
      },
    })
  }

  return (
    <div className={styles.attendeeWrapper}>
      <img src={attendeesIcon} alt="Attendees" className={styles.titleIcon} />
      <div className={`${styles.attendeeContent} ${!editEventData.isMyEvent ? styles.dimmed : ''}`}>
        <div className={styles.attendeeRow}>
          <div className={styles.attendeeSelectContainer}>
            <SharedEmailSelect
              innerRef={selectRef}
              placeholder={
                isRequired
                  ? t('calendar.edit.inviteRequiredAttendee')
                  : t('calendar.edit.inviteOptionalAttendee')
              }
              onChange={handleChange}
              innerDomain={userDomain}
              disabled={!editEventData.isMyEvent}
            />
          </div>

          <div className={styles.attendeeButtons}>
            <button
              className={styles.addButton}
              onClick={() =>
                openSelectUser({ title: t('calendar.edit.invitation'), key: 'invitation' })
              }
            >
              <img src={addIcon} alt="Add" />
            </button>
            {isRequired && !hideOptionalBtn && (
              <button className={styles.optionalButton} onClick={onOptionalClick}>
                {t('calendar.edit.optional')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AttendeeSelector
